/**
 * Mask Registry
 *
 * central registry of all available input masks.
 * each mask is defined in its own file for better organization and testability.
 *
 * usage:
 *   const config = MASK_REGISTRY[maskType](decimalConfig);
 *   const { cleaveOptions, placeholder, formatValue } = config;
 */

import {
  dollarMask,
  floatMask,
  dollarFourDecMask,
  totalDollarMask,
} from "./currency";
import {
  phoneMask,
  ssnMask,
  zipcodeMask,
  zipcodeFourMask,
  zipcodeNineMask,
  creditcardMask,
  medicareMask,
} from "./formatted";
import { numberMask, ageMask, ageDecimalMask } from "./numeric";
import { noneMask } from "./other";
import type { MaskFactory } from "./types";

// define valid mask types as const array for type safety
export const VALID_MASKS = [
  "phone",
  "zipcode",
  "zipcode-four",
  "ssn",
  "medicare",
  "creditcard",
  "zipcode-nine",
  "number",
  "float",
  "dollar",
  "dollar-four-dec",
  "age",
  "age-decimal",
  "none",
  "total_dollar",
] as const;

// create union type from valid masks
export type MaskType = (typeof VALID_MASKS)[number];

// registry mapping mask type to its factory function
export const MASK_REGISTRY: Record<MaskType, MaskFactory> = {
  // currency masks
  dollar: dollarMask,
  float: floatMask,
  "dollar-four-dec": dollarFourDecMask,
  total_dollar: totalDollarMask,

  // numeric masks
  number: numberMask,
  age: ageMask,
  "age-decimal": ageDecimalMask,

  // formatted masks
  phone: phoneMask,
  ssn: ssnMask,
  zipcode: zipcodeMask,
  "zipcode-four": zipcodeFourMask,
  "zipcode-nine": zipcodeNineMask,
  creditcard: creditcardMask,
  medicare: medicareMask,

  // other
  none: noneMask,
};

export type { MaskConfig, MaskFactory } from "./types";
